// Copyright (c) Docugami, Inc. All rights reserved.
import { useState } from 'react';
import { useDeepLinkInitializer } from '../hooks/deepLinkInitializer';
import { aaCardSeparatorColor, primaryTheme, homeGray } from '../components/shell/presentation/AppStyles';
import { useAaCardClauses, ClauseCardType } from '../hooks/aaCardClauses';
import { clauseCardTypeColorLookup } from '../components/assisted_authoring/AaCard';
import { dgIconNames } from '../hooks/dgIcons';
import { DgIconButton } from '../components/shell/utils/DgIconButton';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { DgFlexCenteredRow, DgHorizontalSpacer, DgFlexRow, DgVerticalSpacer, DgFlexCenteredColumn, DgNonSelectableLabelCss, DgSingleLineEllipsis, DgMultineEllipsis, DgFlexSpacer, DgFlexColumn, DgSelectable } from '../components/common/DgCommonStyledComponents';
import { dgFontFamily, dgTagFontFamily, userFontFamily } from 'components/common/Fonts';

const AaClausesPageActionLabel = styled.span`
    font-family: ${dgFontFamily};
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;

    color: ${homeGray};
`;

const AaClausesPageVerticalDivider = styled.div`
    height: 100%;
    border-right: 0.5px solid ${aaCardSeparatorColor};
`;

interface AaCardColorStripeProps extends DgSelectable {
    readonly cardType: ClauseCardType;
};

const AaClausesPageHeaderAndTextContainer = styled.div<AaCardColorStripeProps>`
    height: 72px;
    width: 300px;
    border-radius: 5px;
    padding: 10px 5px 5px 10px;
    margin-bottom: 7px;
    background: ${props => props.selected ? `${clauseCardTypeColorLookup[props.cardType]}80` : "transparent"};

    :hover {
        background: ${props => `${clauseCardTypeColorLookup[props.cardType]}80`};
    }
`;

const AaClausesPageVariantHeading = styled(DgSingleLineEllipsis)`
    font-family: ${dgTagFontFamily};
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 19px;
    color: ${primaryTheme.palette.black};
    ${DgNonSelectableLabelCss}
`;

const AaClausesPageVariantText = styled(DgMultineEllipsis)`
    font-family: ${userFontFamily};
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 15px;
    padding-bottom: 1px;
    color: ${primaryTheme.palette.black};
    ${DgNonSelectableLabelCss}
`;

const AaClausesPageVariantList = styled(DgFlexCenteredColumn)`
    overflow-y: auto;
    width: 332px;
`;

const PreviewTextContainer = styled.div`
    display: flex;
    flex: 1;
    padding: 10px 50px;
`;

const AaClausesPagePreviewText = styled.span`
    font-family: ${userFontFamily};
    font-style: normal;
    font-weight: 300;
    font-size: 12px;
    line-height: 15px;
    color: ${primaryTheme.palette.black};
`;

const AaClausesPageContent = styled(DgFlexColumn)`
    width: 100%;
    overflow: hidden;
    background: ${primaryTheme.palette.white};
`;

const AAButtonContainer = styled(DgFlexCenteredRow) <{ color: string }>`
    background-color: ${props => `${props.color}80`};
    justify-content: flex-start;
    height: 26px;
    padding: 5px 7px;
    width: auto;
    cursor: pointer;
    border-radius: 5px;
    box-sizing: border-box;
`;

interface aaCardActionButton {
    iconName: dgIconNames;
    label: string;
    iconHeight: number;
    iconWidth: number;
}

export function AaClausesPage(): JSX.Element {
    const params = useParams();
    // @ts-ignore
    const [selectedFragmentIdx, setSelectedFragmentIdx] = useState(parseInt(params['selectedClause']));

    useDeepLinkInitializer();
    let aaCardClauses = useAaCardClauses(null);

    const aaCardActionButtonPropsForCard: { [current: number]: aaCardActionButton } = {
        [ClauseCardType.MissingClause]: {
            iconName: dgIconNames.AssistedAuthoringPaste,
            iconHeight: 16,
            iconWidth: 16,
            label: "Insert"
        },

        [ClauseCardType.VariantClause]: {
            iconName: dgIconNames.AssistedAuthoringReplace,
            iconHeight: 16,
            iconWidth: 16,
            label: "Replace"
        },

        [ClauseCardType.ReviewClause]: {
            iconName: dgIconNames.AssistedAuthoringClear,
            iconHeight: 17,
            iconWidth: 19,
            label: "Remove"
        },
    }

    function header(): JSX.Element[] | null {
        let data = aaCardClauses.clauseData();
        if (!data) {
            return null;
        }

        let actionProps = aaCardActionButtonPropsForCard[data.cardType];
        return [
            <DgVerticalSpacer height={"7px"} />,
            <DgFlexCenteredRow>
                <DgHorizontalSpacer width={"10px"} />
                <AAButtonContainer color={clauseCardTypeColorLookup[data.cardType]} onClick={() => aaCardClauses.collapsePopup(true)}>
                    <DgIconButton
                        iconName={actionProps.iconName}
                        height={actionProps.iconHeight}
                        width={actionProps.iconWidth}
                        color={homeGray}
                        shadow=''
                    />
                    <DgHorizontalSpacer width={"10px"} />
                    <AaClausesPageActionLabel>{actionProps.label}</AaClausesPageActionLabel>
                </AAButtonContainer>
            </DgFlexCenteredRow>]
    }

    function previewText(): JSX.Element | null {
        let data = aaCardClauses.clauseData();
        if (!data) {
            return null;
        }

        let text: string = '';
        if (selectedFragmentIdx === -1) {
            text = data.text;
        }
        else if (data.fragments) {
            text = data.fragments[selectedFragmentIdx].rawText as string;
        }

        return (
            <PreviewTextContainer>
                <AaClausesPagePreviewText>{text.trim()}</AaClausesPagePreviewText>
            </PreviewTextContainer>
        )
    }

    function variantsCards(): JSX.Element[] | null {
        const data = aaCardClauses.clauseData();
        if (!data || !data.fragments) {
            return null;
        }

        return [
            cardLabelAndText(data.cardType, data.header, data.text, -1),
            ...data.fragments.map((f, idx) => cardLabelAndText(data.cardType, data.header, f.rawText as string, idx))
        ]
    }

    function cardLabelAndText(
        cardType: ClauseCardType,
        header: string,
        text: string,
        fragmentIdx: number): JSX.Element {
        return (
            <AaClausesPageHeaderAndTextContainer
                selected={selectedFragmentIdx === fragmentIdx}
                cardType={cardType}
                onClick={() => {
                    setSelectedFragmentIdx(fragmentIdx);
                    aaCardClauses.selectFragment(fragmentIdx);
                }}>
                <AaClausesPageVariantHeading>{header}</AaClausesPageVariantHeading>
                <DgVerticalSpacer height={"3px"} />
                <AaClausesPageVariantText numLines={3}>{text}</AaClausesPageVariantText>
            </AaClausesPageHeaderAndTextContainer>
        )
    }

    return (
        <AaClausesPageContent>
            {header()}
            <DgFlexRow style={{ flex: 1 }}>
                <DgFlexSpacer>
                    {previewText()}
                </DgFlexSpacer>
                <AaClausesPageVerticalDivider />
                <AaClausesPageVariantList>
                    {variantsCards()}
                </AaClausesPageVariantList>
            </DgFlexRow>
        </AaClausesPageContent>
    )
}
